import memoize from 'lodash.memoize';
import { createSelector, createStructuredSelector } from 'reselect';
import * as technical from './iiif/technical';
import * as descriptive from './iiif/descriptive';
import * as linking from './iiif/linking';
import * as paging from './iiif/paging';
import * as structural from './iiif/structural';
import { getAllCollections, getAllLayers, getAllServices, getAllExternalResources, getAllManifests, getAllAnnotationLists, getAllImages } from '../all';
import mapById from '../../utility/mapById';
import mapAllById from '../../utility/mapAllById';

var collection = memoize(function (selector) {
  /**************************************************
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingHint
   * - getNavDate
   **************************************************/
  var getId = createSelector(selector, technical.getId);
  var getType = createSelector(selector, technical.getType);
  var getViewingHint = createSelector(selector, technical.getWhitelistedViewingHint(['individuals', 'multi-part']));
  var getNavDate = createSelector(selector, technical.getNavDate);

  /**************************************************
   * Descriptive properties
   *
   * - getLabel
   * - getDescription
   * - getMetadata
   * - getAttribution
   * - getLogo
   * - getLicense
   * - getThumbnail
   **************************************************/
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getDescription = createSelector(selector, descriptive.getDescription);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getAttribution = createSelector(selector, descriptive.getAttribution);

  var getLogo = createSelector(selector, descriptive.getLogo);

  var getLicense = createSelector(selector, descriptive.getLicense);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);
  var getThumbnail = createSelector(getThumbnailId, getAllImages, function (thumbnailId, allImages) {
    return allImages[thumbnailId] || thumbnailId;
  });

  /**************************************************
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getRelated
   * - getRendering
   * - getWithin
   **************************************************/
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllExternalResources, mapAllById);

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, mapAllById);

  var getRelatedIds = createSelector(selector, linking.getRelated);
  var getRelated = createSelector(getRelatedIds, getAllExternalResources, mapAllById);

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllExternalResources, mapAllById);

  var getWithinIds = createSelector(selector, linking.getWithin);
  var getWithin = createSelector(getWithinIds, getAllLayers, getAllExternalResources, function (withinIds, allLayers, allExternalResources) {
    return withinIds.map(function (within) {
      return allLayers[within.id] || allExternalResources[within.id];
    }).filter(function (e) {
      return e;
    });
  });

  /**************************************************
   * Paging properties
   *
   * - getFirst
   * - getLast
   * - getTotal
   * - getNext
   * - getPrevious
   * - getStartIndex
   **************************************************/
  var getFirstId = createSelector(selector, paging.getFirst);
  var getFirst = createSelector(getFirstId, getAllCollections, mapById);

  var getLastId = createSelector(selector, paging.getLast);
  var getLast = createSelector(getLastId, getAllCollections, mapById);

  var getTotal = createSelector(selector, paging.getTotal);

  var getNextId = createSelector(selector, paging.getNext);
  var getNext = createSelector(getNextId, getAllCollections, mapById);

  var getPreviousId = createSelector(selector, paging.getPrevious);
  var getPrevious = createSelector(getPreviousId, getAllCollections, mapById);

  var getStartIndex = createSelector(selector, paging.getStartIndex);

  /**************************************************
   * Structural properties
   *
   * - getCollections
   * - getManifests
   * - getMembers
   * - getOtherContent
   **************************************************/
  var getMemberIds = createSelector(selector, structural.getMembers);

  var getCollectionIds = createSelector(selector, structural.getCollections);
  var getCollections = createSelector(getCollectionIds, getAllCollections, mapAllById);

  var getManifestIds = createSelector(selector, structural.getManifests);
  var getManifests = createSelector(getMemberIds, getAllManifests, function (memberIds, allManifests) {
    return memberIds.map(function (memberId) {
      return allManifests[memberId] || null;
    }).filter(Boolean);
  });

  var getMembers = createSelector(getMemberIds, getAllCollections, getAllManifests, function (memberIds, allCollections, allManifests) {
    return memberIds.map(function (memberId) {
      return allCollections[memberId] || allManifests[memberId];
    }).filter(Boolean);
  });
  var getOtherContentIds = createSelector(selector, structural.getOtherContent);
  var getOtherContent = createSelector(getOtherContentIds, getAllExternalResources, getAllAnnotationLists, function (otherContentIds, allExternalResources, allAnnotationLists) {
    return otherContentIds.map(function (otherContentId) {
      return allAnnotationLists[otherContentId] || allExternalResources[otherContentId] || {
        '@id': otherContentId,
        label: [{ '@value': 'unknown', '@language': '@none' }]
      };
    });
  });

  return {
    // Technical
    getId: getId,
    getType: getType,
    getViewingHint: getViewingHint,
    getNavDate: getNavDate,
    // Descriptive
    getLabel: getLabel,
    getDescription: getDescription,
    getMetadata: getMetadata,
    getAttribution: getAttribution,
    getLogo: getLogo,
    getLicense: getLicense,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    // Linking
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    getServiceIds: getServiceIds,
    getService: getService,
    getRelatedIds: getRelatedIds,
    getRelated: getRelated,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getWithinIds: getWithinIds,
    getWithin: getWithin,
    // Paging
    getFirstId: getFirstId,
    getFirst: getFirst,
    getLastId: getLastId,
    getLast: getLast,
    getTotal: getTotal,
    getNextId: getNextId,
    getNext: getNext,
    getPreviousId: getPreviousId,
    getPrevious: getPrevious,
    getStartIndex: getStartIndex,
    // Structural
    getCollectionIds: getCollectionIds,
    getCollections: getCollections,
    getManifestIds: getManifestIds,
    getManifests: getManifests,
    getMemberIds: getMemberIds,
    getMembers: getMembers,
    getOtherContentIds: getOtherContentIds,
    getOtherContent: getOtherContent
    // Other
  };
});

export default collection;